/**
 * Phase 4 — Admin content reports + pending review.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { AdminReportsOverview } from '@/lib/types';

export default function ReportsPage() {
  const [data, setData] = useState<AdminReportsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await apiClient.getAdminReports();
    if (res.success && res.data) setData(res.data as AdminReportsOverview);
    else setError(res.error || 'Failed to load reports (admin only)');
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-amber-700">Governance</p>
        <h1 className="mt-0.5 text-xl font-bold text-slate-900">Content reports</h1>
        <p className="text-sm text-slate-500 mt-1">Lifecycle counts, pending review, and completeness.</p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.counts).map(([key, value]) => (
              <div key={key} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{key.replace('_', ' ')}</p>
                <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
              </div>
            ))}
          </div>

          <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Pending review</h2>
            {data.pending_review.length === 0 ? (
              <p className="text-sm text-slate-400">No courses awaiting approval.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.pending_review.map(item => (
                  <li key={item.id} className="py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{item.title}</p>
                      <p className="text-xs text-slate-400">{item.tutor}</p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/courses/${item.id}/edit?tab=settings`} className="text-xs text-blue-600 underline">
                        Open
                      </Link>
                      <button
                        onClick={async () => {
                          await apiClient.approveCourse(item.id);
                          load();
                        }}
                        className="rounded bg-emerald-600 px-2 py-1 text-xs font-semibold text-white"
                      >
                        Approve
                      </button>
                      <button
                        onClick={async () => {
                          const note = prompt('Rejection note') || '';
                          await apiClient.rejectCourse(item.id, note);
                          load();
                        }}
                        className="rounded bg-amber-600 px-2 py-1 text-xs font-semibold text-white"
                      >
                        Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Stale drafts</h2>
            <p className="text-xs text-slate-400 mb-3">Not updated in {data.stale_days}+ days</p>
            {data.stale_drafts.length === 0 ? (
              <p className="text-sm text-slate-400">None</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.stale_drafts.map(item => (
                  <li key={item.id} className="py-2 flex justify-between text-sm">
                    <Link to={`/courses/${item.id}/edit`} className="font-medium text-blue-600 hover:underline">
                      {item.title}
                    </Link>
                    <span className="text-xs text-slate-400">{item.status} · {item.tutor}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Incomplete courses</h2>
            {data.incomplete_courses.length === 0 ? (
              <p className="text-sm text-slate-400">All courses look reasonably complete.</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500">
                    <th className="py-2">Course</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Tutor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.incomplete_courses.map(item => (
                    <tr key={item.id}>
                      <td className="py-2">
                        <Link to={`/courses/${item.id}/edit`} className="text-blue-600 hover:underline">
                          {item.title}
                        </Link>
                      </td>
                      <td>{item.status}</td>
                      <td>{item.completeness}%</td>
                      <td className="text-slate-500">{item.tutor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
